import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import type { MJUserNotificationEntity } from '@memberjunction/core-entities';
import { MJNotificationService } from '@memberjunction/ng-notifications';
import { OEPSaveError, OEPSaveMessage } from '../data/oep-data.service';
import { OEPViewBase } from '../state/oep-view-base';

/** US Eastern, e.g. "Sep 30, 2026, 9:41 AM" (reporting convention: times in ET). */
const WHEN = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short' });

export interface OEPNotificationItem { ID: string; Title: string; Message: string; When: string; Unread: boolean }

/**
 * The notifications panel (markup.html:167-195 copy) in MJ's mj-slide-panel. Items are the
 * signed-in user's MJ: User Notifications, streamed by MJNotificationService.notifications$
 * (UserInfoEngine's cache). Opening one marks it read by saving the cached entity itself, so
 * the engine cache — and therefore the header's unread count — updates without a reload.
 */
@Component({
    standalone: false,
    selector: 'oep-notifications',
    templateUrl: './oep-notifications.component.html',
    styleUrl: './oep-notifications.component.scss',
})
export class OEPNotificationsComponent extends OEPViewBase implements OnDestroy {
    @Input() Open = false;
    @Output() Close = new EventEmitter<void>();
    @Output() OpenEmails = new EventEmitter<void>();
    Items: OEPNotificationItem[] = [];
    private entities = new Map<string, MJUserNotificationEntity>();
    private sub: Subscription | null = null;

    protected async Load(): Promise<void> {
        this.sub?.unsubscribe();
        this.sub = MJNotificationService.Instance.notifications$.subscribe((rows) => {
            this.entities = new Map(rows.map((n) => [n.ID, n]));
            this.Items = rows.map((n) => ({
                ID: n.ID,
                Title: n.Title ?? '',
                Message: n.Message ?? '',
                When: WHEN.format(n.__mj_CreatedAt),
                Unread: n.Unread,
            }));
            this.Changed();
        });
    }

    override ngOnDestroy(): void {
        this.sub?.unsubscribe();
        this.sub = null;
        super.ngOnDestroy();
    }

    get IsStaff(): boolean { return this.Context.IsStaff; }

    async Pick(item: OEPNotificationItem): Promise<void> {
        const n = this.entities.get(item.ID);
        if (n && n.Unread && !this.Busy) {
            // Not Act(): marking read changes no OEP counts, so the context needs no reload.
            this.Busy = true;
            this.Changed();
            try {
                n.Unread = false;
                n.ReadAt = new Date();
                if (!(await n.Save())) throw new OEPSaveError(OEPSaveMessage(n.LatestResult));
            } catch (err) {
                MJNotificationService.Instance.CreateSimpleNotification(err instanceof Error ? err.message : String(err), 'error', 5000);
            } finally {
                this.Busy = false;
                this.Changed();
            }
        }
        this.Close.emit();
    }
}
